import { SeoController } from '@application/components';
import {
  filterRegionOptions,
  generateItemsWithPromos,
  useModal,
  useRouter,
} from '@application/utils';
import type { CountryCode } from '@gql/graphql';
import type { ShelterListPageFormValues } from '@presentation/pages';
import { ShelterListPage } from '@presentation/pages';
import { FormikProvider, useFormik } from 'formik';
import { useGetStorePromos } from '@application/api';
import hash from 'object-hash';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useFormatShelters } from './api/useFormatShelters';
import { useGetShelters } from './api/useGetShelters';
import { useShelterWhereBuilder } from './api/useShelterWhereBuilder';
import { FORM_INITIAL_VALUES, PAGINATED_SIZE } from './constants';
import { useSearch } from './useSearch';

export const ShelterListPageController: FC = () => {
  const { translate, countryCode, infoFromCurrentPath } = useRouter();
  const {
    shelters,
    sheltersCount,
    sheltersLoading,
    sheltersFetchMore,
    sheltersFetchMoreLoading,
    sheltersRefetch,
  } = useGetShelters();
  const { formattedShelters } = useFormatShelters(shelters);
  const { modal: filtersModal, showFromClick: filtersShow } = useModal();

  const [size, setSize] = useState(PAGINATED_SIZE);
  const { setSearchFromValues, valuesFromSearch } = useSearch();
  const { shelterWhereBuilder } = useShelterWhereBuilder();

  const form = useFormik<ShelterListPageFormValues>({
    enableReinitialize: true,
    initialValues: valuesFromSearch,
    onSubmit: (values) => {
      setSearchFromValues(values);
      sheltersRefetch({ where: shelterWhereBuilder(values) });
    },
  });

  const formValuesChanged = useMemo(() => {
    const hashInitialValues = hash(FORM_INITIAL_VALUES);
    const hashValues = hash(form.values);
    return hashValues !== hashInitialValues;
  }, [form.values]);

  const regionOptions = filterRegionOptions(
    translate,
    countryCode,
    infoFromCurrentPath.search.countries as CountryCode[],
  );

  const { listingPromoItems } = useGetStorePromos('shelters');
  const items = useMemo(() => {
    return generateItemsWithPromos(formattedShelters ?? [], listingPromoItems, PAGINATED_SIZE);
  }, [formattedShelters, listingPromoItems]);

  return (
    <>
      <SeoController
        title={translate('shelterList.seo.title')}
        description={translate('shelterList.seo.description')}
        keywords={translate('shelterList.seo.keywords')}
      />
      <FormikProvider value={form}>
        <ShelterListPage
          options={{
            regions: regionOptions,
          }}
          items={items.list}
          loading={sheltersLoading}
          canLoadMore={size < sheltersCount}
          loadMoreText={`${Math.min(shelters?.length ?? 0, sheltersCount)} / ${sheltersCount}`}
          onLoadMoreClick={async () => {
            await sheltersFetchMore({
              variables: {
                skip: size,
              },
            });

            setSize(size + PAGINATED_SIZE);
          }}
          loadMoreLoading={sheltersFetchMoreLoading}
          filtersModal={{
            modal: filtersModal,
            onShowClick: filtersShow,
            loading: sheltersLoading,
            canReset: formValuesChanged,
            onResetClick: () => {
              setSearchFromValues(FORM_INITIAL_VALUES);
              sheltersRefetch({ where: shelterWhereBuilder(FORM_INITIAL_VALUES) });
            },
          }}
        />
      </FormikProvider>
    </>
  );
};
